import useSWR from 'swr';
import { api } from '@utils/api';

// Get admin token from localStorage
const getAdminHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
    };
};

// Admin fetcher with auth
const adminFetcher = async (url) => {
    const response = await fetch(api(url), {
        headers: getAdminHeaders()
    });
    
    if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
            localStorage.removeItem('adminToken');
            localStorage.removeItem('adminData');
            window.location.href = '/admin-login';
            throw new Error('Unauthorized');
        }
        throw new Error('API request failed');
    }
    
    return response.json();
};

// Hook for admin users list
export const useAdminUsers = (search = '', status = '', page = 1, limit = 50) => {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (status) params.append('status', status);
    params.append('page', page);
    params.append('limit', limit);
    
    const { data, error, isLoading, mutate } = useSWR(
        `/api/admin/users?${params.toString()}`,
        adminFetcher,
        {
            revalidateOnFocus: false,
            revalidateOnReconnect: false, // ✅ Don't refetch on reconnect
            dedupingInterval: 60000, // ✅ Increased to 60 seconds
            keepPreviousData: true // ✅ Keep previous data while loading
        }
    );
    
    return {
        users: data?.users || [],
        pagination: data?.pagination || {},
        isLoading,
        isError: error,
        mutate
    };
};

// Hook for VIP users
export const useVIPUsers = (search = '', statusFilter = '', page = 1) => {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (statusFilter) params.append('status', statusFilter);
    params.append('page', page);
    params.append('limit', '30'); // ✅ Reduced from 50
    
    const { data, error, isLoading, mutate } = useSWR(
        `/api/admin/vip/users?${params.toString()}`,
        adminFetcher,
        {
            revalidateOnFocus: false,
            revalidateOnReconnect: false, // ✅ Don't refetch on reconnect
            dedupingInterval: 60000, // ✅ Increased to 60 seconds
            keepPreviousData: true
        }
    );
    
    return {
        users: data?.users || [],
        pagination: data?.pagination || {},
        isLoading,
        isError: error,
        mutate
    };
};

// Hook for VIP transactions
export const useVIPTransactions = (search = '', status = '', page = 1) => {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (status) params.append('status', status);
    params.append('page', page);
    params.append('limit', '15'); // ✅ Reduced from 20
    
    const { data, error, isLoading, mutate } = useSWR(
        `/api/admin/vip/transactions?${params.toString()}`,
        adminFetcher,
        {
            revalidateOnFocus: false,
            revalidateOnReconnect: false, // ✅ Don't refetch on reconnect
            dedupingInterval: 60000, // ✅ Increased to 60 seconds
            keepPreviousData: true
        }
    );
    
    return {
        transactions: data?.transactions || [],
        pagination: data?.pagination || {},
        isLoading,
        isError: error,
        mutate
    };
};

// Hook for video ads
export const useVideoAds = () => {
    const { data, error, isLoading, mutate } = useSWR(
        '/api/admin/ads',
        adminFetcher,
        {
            revalidateOnFocus: false,
            revalidateOnReconnect: false, // ✅ Don't refetch on reconnect
            dedupingInterval: 120000, // ✅ Increased to 2 minutes
            keepPreviousData: true
        }
    );
    
    return {
        ads: data?.data || [],
        isLoading,
        isError: error,
        mutate
    };
};

// Hook for dashboard stats (without SWR to prevent auto-refresh)
export const useDashboardStats = () => {
    const { data, error, isLoading } = useSWR(
        '/api/admin/users?limit=1',
        adminFetcher,
        {
            revalidateOnFocus: false,
            revalidateOnReconnect: false
        }
    );
    
    // Calculate stats from the response
    const totalUsers = data?.pagination?.total || 0;
    
    return {
        stats: {
            totalUsers,
            vipUsers: 0, // Will be populated from separate endpoint
            activeToday: 0,
            onlineUsers: 0
        },
        isLoading,
        isError: error
    };
};

// Admin API helper functions
export const adminApi = {
    // User management
    async getUser(userId) {
        const response = await fetch(api(`/api/admin/users/${userId}`), {
            headers: getAdminHeaders()
        });
        return response.json();
    },
    
    async updateUser(userId, data) {
        const response = await fetch(api(`/api/admin/users/${userId}`), {
            method: 'PATCH',
            headers: getAdminHeaders(),
            body: JSON.stringify(data)
        });
        return response.json();
    },

    async resetUserPassword(userId, newPassword) {
        const response = await fetch(api(`/api/admin/users/${userId}/reset-password`), {
            method: 'POST',
            headers: getAdminHeaders(),
            body: JSON.stringify({ newPassword })
        });
        return response.json();
    },

    async adjustUserVIP(userId, adjustDays) {
        const response = await fetch(api(`/api/admin/users/${userId}/adjust-vip`), {
            method: 'POST',
            headers: getAdminHeaders(),
            body: JSON.stringify({ adjustDays })
        });
        return response.json();
    },
    
    // VIP management
    async grantVIP(email, packageCode) {
        const response = await fetch(api('/api/admin/vip/grant'), {
            method: 'POST',
            headers: getAdminHeaders(),
            body: JSON.stringify({ email, package_code: packageCode })
        });
        return response.json();
    },
    
    async updateVIPUser(userId, data) {
        const response = await fetch(api(`/api/admin/vip/users/${userId}`), {
            method: 'PATCH',
            headers: getAdminHeaders(),
            body: JSON.stringify(data)
        });
        return response.json();
    },
    
    async approveTransaction(transactionId) {
        const response = await fetch(api(`/api/admin/vip/transactions/${transactionId}`), {
            method: 'PATCH',
            headers: getAdminHeaders(),
            body: JSON.stringify({ action: 'approve' })
        });
        return response.json();
    },
    
    async rejectTransaction(transactionId, note) {
        const response = await fetch(api(`/api/admin/vip/transactions/${transactionId}`), {
            method: 'PATCH',
            headers: getAdminHeaders(),
            body: JSON.stringify({ action: 'reject', note })
        });
        return response.json();
    },
    
    // Ads management
    async createAdUploadUrl(fileName, contentType) {
        const response = await fetch(api('/api/admin/ads/upload-url'), {
            method: 'POST',
            headers: getAdminHeaders(),
            body: JSON.stringify({ fileName, contentType })
        });
        return response.json();
    },
    
    async createAd(data) {
        const response = await fetch(api('/api/admin/ads'), {
            method: 'POST',
            headers: getAdminHeaders(),
            body: JSON.stringify(data)
        });
        return response.json();
    },
    
    async toggleAdActive(adId, isActive) {
        const response = await fetch(api(`/api/admin/ads/${adId}`), {
            method: 'PATCH',
            headers: getAdminHeaders(),
            body: JSON.stringify({ is_active: isActive })
        });
        
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ message: 'Failed to toggle ad' }));
            throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
        }
        
        return response.json();
    },
    
    async deleteAd(adId) {
        const response = await fetch(api(`/api/admin/ads/${adId}`), {
            method: 'DELETE',
            headers: getAdminHeaders()
        });
        return response.json();
    },
    
    // Banner management
    async getBanners() {
        const response = await fetch(api('/api/admin/banners'), {
            headers: getAdminHeaders()
        });
        return response.json();
    },
    
    async updateBanners(banners) {
        const response = await fetch(api('/api/admin/banners'), {
            method: 'POST',
            headers: getAdminHeaders(),
            body: JSON.stringify(banners)
        });
        return response.json();
    }
};

